'use client';
// Pastille normative (F1) : « pourquoi fait-on ça ? », là où on le fait.
//
// Le motif existait déjà en deux exemplaires — infobulle des règles de Westgard,
// composant `Help` pour les CAPA. Il est ici généralisé sur un référentiel
// unique (`shared/iso15189`), pour que l'explication d'une clause soit écrite
// une fois et lue partout où cette clause s'applique.
//
// Discrétion volontaire : la pastille se voit sans s'imposer. Un RQ chevronné
// n'a pas à subir la pédagogie du débutant, mais elle doit rester à un clic.
import React, { useState } from 'react';
import { useGed } from '@/lib/store';
import { clause, COUVERTURE_META, NUMEROTATION_VERIFIEE } from '@/shared/iso15189';
import { VOILE, I, useEscape } from '@/components/ui';
import type { GedView } from '@/lib/types';

/**
 * Rendu du gras `**…**` des textes du référentiel. Ces emphases ne sont pas
 * décoratives : elles marquent les écarts produit (« **Manque** … »), et les
 * afficher avec leurs astérisques donnait un texte qui a l'air mal relu.
 */
export function Texte({ v }: { v: string }) {
  return (
    <>
      {v.split(/(\*\*[^*]+\*\*)/g).map((bout, i) =>
        bout.startsWith('**') && bout.endsWith('**')
          ? <strong key={i} style={{ color: 'var(--text-strong)' }}>{bout.slice(2, -2)}</strong>
          : <React.Fragment key={i}>{bout}</React.Fragment>)}
    </>
  );
}

const CHAMPS: { cle: 'exigence' | 'pourquoi' | 'chezVous' | 'preuve'; titre: string }[] = [
  { cle: 'exigence', titre: 'Ce que la norme demande' },
  { cle: 'pourquoi', titre: 'Pourquoi cette exigence existe' },
  { cle: 'chezVous', titre: 'Comment BIO24 y répond' },
  { cle: 'preuve', titre: 'Ce que l’auditeur demandera' },
];

export function Norme({ art }: { art: string }) {
  const [ouvert, setOuvert] = useState(false);
  const { go, openDoc } = useGed();
  const c = clause(art);
  useEscape(ouvert, () => setOuvert(false));
  // Une pastille sur une clause absente du référentiel serait un lien mort :
  // mieux vaut ne rien afficher que promettre une explication inexistante.
  if (!c) return null;
  const cov = COUVERTURE_META[c.couverture];

  return (
    <>
      <button
        onClick={() => setOuvert(true)}
        title={`ISO 15189 §${c.ref} — ${c.titre}. Cliquez pour l’explication.`}
        aria-label={`Explication de l’article ${c.ref} — ${c.titre}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, verticalAlign: 'baseline',
          background: 'var(--cyan-50)', color: 'var(--cyan-700)', border: '1px solid var(--cyan-200)',
          borderRadius: 'var(--radius-pill)', padding: '1px 8px', marginLeft: 6, cursor: 'pointer',
          fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 700, lineHeight: 1.5,
        }}>
        <I n="book" s={11} />§{c.ref}
      </button>

      {ouvert && (
        <div role="dialog" aria-modal="true" aria-label={`ISO 15189 §${c.ref}`}
          // La pastille vit souvent dans un `Eyebrow` : police à chasse fixe,
          // capitales, lettres espacées. `position:fixed` ne coupe PAS
          // l'héritage typographique — sans cette remise à zéro, tout le
          // panneau s'affichait en majuscules et en monospace.
      /* Fenêtre de CONSULTATION : le clic à côté referme, c'est le confort
         que la RQ a demandé de garder le 04/09/2026. Rien ne s'y saisit. */
          onClick={() => setOuvert(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 950, background: VOILE, display: 'flex', justifyContent: 'flex-end', fontFamily: 'var(--font-sans)', textTransform: 'none', letterSpacing: 'normal', fontWeight: 400, textAlign: 'left' }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(520px, 100%)', height: '100%', background: '#fff', overflowY: 'auto', boxShadow: 'var(--shadow-lg, 0 8px 40px rgba(14,39,64,0.25))', display: 'grid', gap: 16, alignContent: 'start', padding: 22 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>ISO 15189:2022 · §{c.ref}</div>
                <h2 style={{ margin: '4px 0 0', fontSize: 17.5, fontWeight: 700, color: 'var(--text-strong)' }}>{c.titre}</h2>
              </div>
              <button onClick={() => setOuvert(false)} aria-label="Fermer"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
                <I n="x" s={18} />
              </button>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span title={cov.aide}
                style={{ display: 'inline-flex', padding: '2px 10px', borderRadius: 'var(--radius-pill)', fontSize: 11, fontWeight: 700, cursor: 'help', background: c.couverture === 'bon' ? '#E7F5EC' : c.couverture === 'manquant' ? 'var(--red-50)' : 'var(--gray-50)', color: c.couverture === 'bon' ? 'var(--green-600)' : c.couverture === 'manquant' ? 'var(--red-600)' : 'var(--text-muted)' }}>
                {cov.label}
              </span>
              <button onClick={() => { setOuvert(false); go('iso' as GedView); }}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary-active)' }}>
                Voir toute la correspondance ISO →
              </button>
            </div>

            {/* Ce que l'écran ne doit jamais taire : ce texte est une proposition
                tant que le RQ ne l'a pas endossé. */}
            {!c.valide && (
              <div style={{ background: 'var(--amber-50, #FFF8EC)', color: 'var(--amber-700, #8A5B00)', borderRadius: 'var(--radius-md)', padding: '10px 13px', fontSize: 12.5, lineHeight: 1.55 }}>
                <strong>Rédaction proposée, non validée par le RQ.</strong> Ce commentaire engage
                l’interprétation du laboratoire : à relire avant d’en faire une référence interne.
                {!NUMEROTATION_VERIFIEE && ' La numérotation des articles reste elle aussi à confronter à l’exemplaire du laboratoire.'}
              </div>
            )}

            {CHAMPS.map(({ cle, titre }) => (
              <div key={cle}>
                <h3 style={{ margin: '0 0 5px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>{titre}</h3>
                <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-body)' }}><Texte v={c[cle]} /></p>
              </div>
            ))}

            {c.ecrans.length > 0 && (
              <div>
                <h3 style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Où cela se passe</h3>
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                  {c.ecrans.map((e, i) => e.module === 'qualite' && e.vue ? (
                    <button key={i} onClick={() => { setOuvert(false); go(e.vue as GedView); }} className="ged-btn"
                      style={{ fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, background: 'var(--cyan-50)', color: 'var(--cyan-700)', border: '1px solid var(--cyan-200)', borderRadius: 'var(--radius-sm)', padding: '5px 10px', cursor: 'pointer' }}>
                      {e.label} →
                    </button>
                  ) : (
                    // Les écrans d'un autre module ne sont pas atteignables d'ici :
                    // on les nomme sans faire croire à un lien.
                    <span key={i} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)', background: 'var(--gray-50)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '5px 10px' }}>
                      {e.label} <span style={{ fontWeight: 500 }}>· module {e.module === 'manuel' ? 'Manuel' : 'Site'}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* La norme est inscrite au fonds comme DOCUMENT EXTERNE MAÎTRISÉ
                (DE-DOC-001) : son texte n'y est pas — il ne peut pas y être —,
                mais l'édition appliquée, le détenteur de l'exemplaire sous
                licence et le lieu où il se consulte y sont. C'est ce que le
                §8.3 demande d'un document externe, et c'est ce qu'un auditeur
                vient vérifier ; d'où ce lien, à un clic de chaque clause. */}
            <div style={{ borderTop: '1px dashed var(--border-default)', paddingTop: 12, display: 'grid', gap: 8 }}>
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-faint)', lineHeight: 1.5 }}>
                Le texte de la norme n’est pas reproduit ici : l’ISO 15189 est un document protégé,
                vendu sous licence. Ce panneau donne la référence et l’interprétation du laboratoire —
                l’exemplaire acheté reste la seule source opposable.
              </p>
              <button
                onClick={() => { setOuvert(false); openDoc('DE-DOC-001'); }}
                className="ged-btn"
                style={{ justifySelf: 'start', fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, background: 'var(--gray-25)', color: 'var(--text-body)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '5px 10px', cursor: 'pointer' }}>
                Fiche du référentiel au fonds documentaire — DE-DOC-001 →
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
